import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PickupOption } from '@raptor-fe/core';
import { ServiceUtils } from '../service-utils/service-utils';

/**
 * Reads and manages the configurable pickup option set. The check-in flow consumes the effective set
 * (tenant defaults with branch overrides applied) to render the pickup options offered to staff; the
 * tenant-admin management UI reads, upserts, and removes the tenant-default options keyed by value.
 */
@Injectable({
    providedIn: 'root',
})
export class PickupOptionApiService {
    private readonly http = inject(HttpClient);
    private readonly serviceUtils = inject(ServiceUtils);

    /**
     * Fetches the effective pickup option set for a tenant/branch combination: tenant defaults with
     * branch overrides applied, ordered by display order.
     *
     * @param tenantId - The tenant identifier
     * @param branchId - The branch identifier
     * @returns The effective enabled options
     */
    getEffective(tenantId: string, branchId: string): Observable<PickupOption[]> {
        return this.http.get<PickupOption[]>(`${this.serviceUtils.getBaseUrl('PickupOption', tenantId)}/effective/${branchId}`);
    }

    /**
     * Fetches the tenant-default pickup options (no branch overrides applied), ordered by display order.
     *
     * @param tenantId - The tenant identifier
     * @returns The tenant-default options
     */
    getTenant(tenantId: string): Observable<PickupOption[]> {
        return this.http.get<PickupOption[]>(this.serviceUtils.getBaseUrl('PickupOption', tenantId));
    }

    /**
     * Creates or updates a tenant-default pickup option keyed by its value.
     *
     * @param tenantId - The tenant identifier
     * @param option - The option to upsert
     * @returns The persisted option
     */
    save(tenantId: string, option: PickupOption): Observable<PickupOption> {
        return this.http.put<PickupOption>(this.serviceUtils.getBaseUrl('PickupOption', tenantId), option);
    }

    /**
     * Removes the tenant-default pickup option for a single value. The value is URL-encoded so a value
     * containing a path separator or fragment character cannot corrupt the route.
     *
     * @param tenantId - The tenant identifier
     * @param value - The opaque value identifying the option to remove
     * @returns The empty removal response
     */
    removeTenant(tenantId: string, value: string): Observable<object> {
        return this.http.delete(`${this.serviceUtils.getBaseUrl('PickupOption', tenantId)}/${encodeURIComponent(value)}`);
    }
}
